import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, ArrowLeft, Search, Loader2, MapPin, DollarSign, Plus, X, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DIRECTORY_LOGO = "https://customer-assets.emergentagent.com/job_supportcenter-3/artifacts/t4g4tqes_New%20Branding%20CharactersDesign%20%281200%20x%201200%20px%29%20%28300%20x%20300%20px%29%20%281%20x%201%20in%29%20%281%20x%201%20in%29%20%28350%20x%20350%20px%29%20%2816%29.png";

const CLASSIFIED_CATEGORIES = [
  'For Sale', 'Services', 'Housing', 'Jobs', 'Community', 'Vehicles', 'Electronics', 'Furniture'
];

export default function DirectoryClassifiedsPage() {
  const [classifieds, setClassifieds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showPostForm, setShowPostForm] = useState(false);
  const [posting, setPosting] = useState(false);
  const [newAd, setNewAd] = useState({
    title: '', category: 'For Sale', description: '', price: '', location: '', contact_email: ''
  });

  useEffect(() => {
    loadClassifieds();
  }, []);

  const loadClassifieds = async () => {
    try {
      const res = await fetch(`${API_URL}/api/directory/classifieds?limit=100`);
      setClassifieds(await res.json());
    } catch (error) {
      console.error('Failed to load classifieds:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!newAd.title || !newAd.description) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setPosting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/directory/classifieds`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newAd,
          price: newAd.price ? parseFloat(newAd.price) : null
        })
      });
      
      if (res.ok) {
        toast.success('Ad posted successfully!');
        setShowPostForm(false);
        setNewAd({ title: '', category: 'For Sale', description: '', price: '', location: '', contact_email: '' });
        loadClassifieds();
      } else {
        toast.error('Failed to post ad');
      }
    } catch (error) {
      toast.error('Failed to post ad');
    } finally {
      setPosting(false);
    }
  };

  const filteredClassifieds = classifieds.filter(c => {
    const matchesSearch = c.title.toLowerCase().includes(search.toLowerCase()) ||
                          c.description?.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !selectedCategory || c.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-[#0d1320]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/directory" className="flex items-center gap-3">
              <img src={DIRECTORY_LOGO} alt="AI Horse-Powered" className="w-10 h-10 rounded-lg" />
              <div className="hidden sm:block">
                <span className="font-semibold text-white text-[15px]">AI Horse-Powered</span>
                <p className="text-[11px] text-gray-500">Classifieds</p>
              </div>
            </Link>
            
            <div className="flex items-center gap-6">
              <div className="hidden md:flex items-center gap-5">
                <Link to="/directory" className="text-gray-400 hover:text-white text-sm transition-colors">Directory</Link>
                <Link to="/directory/deals" className="text-gray-400 hover:text-white text-sm transition-colors">Deals</Link>
                <Link to="/directory/jobs" className="text-gray-400 hover:text-white text-sm transition-colors">Jobs</Link>
              </div>
              <Button 
                size="sm" 
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white border-0"
                onClick={() => setShowPostForm(true)}
              >
                <Plus className="w-4 h-4 mr-1" /> Post Ad
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative py-14 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-950/20 via-transparent to-transparent" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-purple-500/5 blur-[100px] rounded-full" />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="w-8 h-8 text-white" />
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-tight">
            Classifieds
          </h1>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Buy, sell, and trade with the AI Horse-Powered community.
          </p>

          {/* Search */}
          <div className="max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search classifieds..."
                className="w-full pl-14 pr-6 py-4 text-[16px] bg-white rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400 shadow-xl shadow-black/20"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 lg:px-6 pb-20">
        {/* Back Link */}
        <Link to="/directory" className="inline-flex items-center gap-2 text-gray-500 hover:text-white text-sm mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Directory
        </Link>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          <Button
            variant={selectedCategory === '' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedCategory('')}
            className={selectedCategory === '' 
              ? 'bg-purple-600 hover:bg-purple-700 text-white border-0' 
              : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:text-white'}
          >
            All
          </Button>
          {CLASSIFIED_CATEGORIES.map((cat) => (
            <Button
              key={cat}
              variant={selectedCategory === cat ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(cat)}
              className={selectedCategory === cat 
                ? 'bg-purple-600 hover:bg-purple-700 text-white border-0' 
                : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:text-white'}
            >
              {cat}
            </Button>
          ))}
        </div>

        {/* Classifieds Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : filteredClassifieds.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl">
            <ShoppingBag className="w-14 h-14 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Listings Found</h3>
            <p className="text-gray-500 mb-4">Be the first to post!</p>
            <Button onClick={() => setShowPostForm(true)} className="bg-purple-600 hover:bg-purple-700 text-white">
              <Plus className="w-4 h-4 mr-2" /> Post an Ad
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredClassifieds.map((ad) => (
              <motion.div
                key={ad.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -3 }}
                className="bg-white rounded-xl p-5 border border-gray-100 hover:border-purple-200 hover:shadow-lg transition-all"
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="px-2.5 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded-lg">
                    {ad.category}
                  </span>
                  {ad.price && (
                    <span className="text-lg font-bold text-emerald-600">
                      ${ad.price.toLocaleString()}
                    </span>
                  )}
                </div>
                
                <h3 className="font-semibold text-gray-900 text-[15px] mb-2">{ad.title}</h3>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{ad.description}</p>
                
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  {ad.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" /> {ad.location}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" /> {new Date(ad.created_at).toLocaleDateString()}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Post Ad Modal */}
      {showPostForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowPostForm(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl"
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-gray-900">Post a Classified Ad</h2>
              <button onClick={() => setShowPostForm(false)} className="text-gray-400 hover:text-gray-600 p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handlePost} className="space-y-4">
              <div>
                <label className="text-sm text-gray-600 mb-1.5 block">Title *</label>
                <Input
                  value={newAd.title}
                  onChange={(e) => setNewAd({...newAd, title: e.target.value})}
                  placeholder="What are you selling?"
                  className="bg-gray-50 border-gray-200"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600 mb-1.5 block">Category *</label>
                <select
                  value={newAd.category}
                  onChange={(e) => setNewAd({...newAd, category: e.target.value})}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm"
                >
                  {CLASSIFIED_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm text-gray-600 mb-1.5 block">Description *</label>
                <Textarea
                  value={newAd.description}
                  onChange={(e) => setNewAd({...newAd, description: e.target.value})}
                  placeholder="Describe your item or service..."
                  rows={4}
                  className="bg-gray-50 border-gray-200"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-600 mb-1.5 block">Price ($)</label>
                  <Input
                    type="number"
                    value={newAd.price}
                    onChange={(e) => setNewAd({...newAd, price: e.target.value})}
                    placeholder="0"
                    className="bg-gray-50 border-gray-200"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1.5 block">Location</label>
                  <Input
                    value={newAd.location}
                    onChange={(e) => setNewAd({...newAd, location: e.target.value})}
                    placeholder="City, State"
                    className="bg-gray-50 border-gray-200"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-600 mb-1.5 block">Contact Email</label>
                <Input
                  type="email"
                  value={newAd.contact_email}
                  onChange={(e) => setNewAd({...newAd, contact_email: e.target.value})}
                  placeholder="your@email.com"
                  className="bg-gray-50 border-gray-200"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700 text-white" disabled={posting}>
                  {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Post Ad'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowPostForm(false)} className="border-gray-300 text-gray-700">
                  Cancel
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-10 px-4 bg-[#070b12] border-t border-white/5">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <img src={DIRECTORY_LOGO} alt="AI Horse-Powered" className="w-8 h-8 rounded-lg" />
            <span className="font-semibold text-white text-sm">AI Horse-Powered Classifieds</span>
          </div>
          <p className="text-sm text-gray-600">© {new Date().getFullYear()} AI Horse-Powered Technology</p>
        </div>
      </footer>
    </div>
  );
}
